import { useTranslation } from 'react-i18next';
import { Box, Divider, useMediaQuery, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useMemo } from 'react';
import { currentContextAtom } from '../../state';
import BackButton from '../../common/components/BackButton';
import { RoutePaths } from '../../routes/AppRoutes';
import { getCompanyShortName } from '../../common/utils/common.utils';
import Breadcrumb from '../../common/components/BreadCrumb';
import { CompanyInfo } from './CompanyInfoCard';
import UnsignedBuyerInfoCard from './UnsignedBuyerInfoCard';
import UnsignedSupplierInfoCard from './UnsignedSupplierInfoCard';
import { ContractPreview } from '../../hooks/useContracts';

export interface UnsignedCompanyInfoCardProps {
  company: CompanyInfo;
  contract?: ContractPreview;
}

export function UnsignedCompanyInfoCard({ company, contract }: UnsignedCompanyInfoCardProps): React.JSX.Element {
  const theme = useTheme();
  const isSmallerThanLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['common', 'contracts']);
  const context = useAtomValue(currentContextAtom);

  const isBuyer = useMemo(() => {
    return company.business_profile === Business_Profile_Enum.Buyer;
  }, [company.business_profile]);

  const backTo = context.realm === Business_Profile_Enum.Buyer
    ? RoutePaths.DISCOVERY_SUPPLIERS
    : RoutePaths.DISCOVERY_BUYERS;

  return (
    <>
      {!isSmallerThanLg && (
        <>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <BackButton
              to={backTo}
              asLink
            />
            <Divider
              orientation="vertical"
              sx={{
                height: '8px',
                mx: '8px',
              }}
            />
            <Breadcrumb
              items={[
                {
                  label: t(context.realm === Business_Profile_Enum.Buyer ? 'discovery:all_suppliers' : 'catalogs:all_stores'), to: backTo,
                  options: {
                    state: {
                      discoveryView: 'SUPPLIERS',
                    },
                  },
                },
                { label: getCompanyShortName(company.tradeName, company.business_profile) },
              ]}
            />
          </Box>
          {isBuyer
            ? (
              <UnsignedBuyerInfoCard company={company} contract={contract} />
            ) : (
              <UnsignedSupplierInfoCard company={company} contract={contract} />
            )}
        </>
      )}

      {isSmallerThanLg && (
        isBuyer
          ? (
            <UnsignedBuyerInfoCard company={company} contract={contract} compact />
          ) : (
            <UnsignedSupplierInfoCard company={company} contract={contract} compact />
          )
      )}
    </>
  );
}
